class NirutiCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-nrt-slide]')];
    this.dots = [...this.querySelectorAll('[data-nrt-dot]')];
    this.previousButton = this.querySelector('[data-nrt-previous]');
    this.nextButton = this.querySelector('[data-nrt-next]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval || 5000);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true));
    this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start());
    this.start();
  }

  disconnectedCallback() { this.stop(); }

  show(nextIndex, restart = false) {
    if (this.slides.length < 2) return;
    const normalized = (nextIndex + this.slides.length) % this.slides.length;
    if (normalized === this.index) return;
    const previous = this.index;
    this.index = normalized;

    this.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.index);
      slide.classList.toggle('is-previous', index === previous && index !== this.index);
    });
    window.setTimeout(() => this.slides[previous]?.classList.remove('is-previous'), 1250);

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (restart) this.start();
  }

  start() {
    this.stop();
    if (this.slides.length > 1 && !this.reducedMotion.matches) {
      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('niruti-carousel')) customElements.define('niruti-carousel', NirutiCarousel);

class NirutiReviewLightbox extends HTMLElement {
  connectedCallback() {
    this.image = this.querySelector('[data-nrt-review-image]');
    this.closeButton = this.querySelector('[data-nrt-review-close]');
    this.section = this.closest('.nrt-community');
    this.openers = [...(this.section?.querySelectorAll('[data-nrt-review-open]') || [])];
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleBackdrop = this.handleBackdrop.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.openers.forEach((opener) => opener.addEventListener('click', this.open));
    this.closeButton?.addEventListener('click', this.close);
    this.addEventListener('click', this.handleBackdrop);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.openers?.forEach((opener) => opener.removeEventListener('click', this.open));
    this.closeButton?.removeEventListener('click', this.close);
    this.removeEventListener('click', this.handleBackdrop);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  open(event) {
    event.preventDefault();
    const opener = event.currentTarget;
    const source = opener.getAttribute('href');
    if (!source || !this.image) return;
    const thumbnail = opener.querySelector('img');
    this.lastFocus = opener;
    this.image.src = source;
    this.image.alt = thumbnail?.alt || 'Original Niruti customer review';
    this.hidden = false;
    document.body.classList.add('nrt-lightbox-open');
    this.closeButton?.focus();
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('nrt-lightbox-open');
    if (this.image) this.image.src = '';
    this.lastFocus?.focus();
  }

  handleBackdrop(event) { if (event.target === this) this.close(); }
  handleKeydown(event) { if (event.key === 'Escape') this.close(); }
}

if (!customElements.get('niruti-review-lightbox')) customElements.define('niruti-review-lightbox', NirutiReviewLightbox);

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches) return;

  const selector = [
    '.nrt-occasion',
    '.nrt-weave',
    '.nrt-all-weave',
    '.nrt-product-card',
    '.nrt-proof',
    '.nrt-instagram',
    '.nrt-review',
    '.nrt-visit__card',
    '.nrt-loom',
    '.nrt-founder__image',
    '.nrt-award'
  ].join(',');

  document.documentElement.classList.add('nrt-motion-ready');

  const reveal = (element) => {
    element.classList.add('nrt-image-in-view');
    element.addEventListener('animationend', () => {
      element.classList.remove('nrt-image-reveal', 'nrt-image-in-view');
      element.style.removeProperty('--nrt-reveal-delay');
    }, { once: true });
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: .12, rootMargin: '0px 0px -7% 0px' })
    : null;

  const prepare = (root = document) => {
    root.querySelectorAll(selector).forEach((element, index) => {
      if (element.classList.contains('nrt-image-reveal')) return;
      element.classList.add('nrt-image-reveal');
      element.style.setProperty('--nrt-reveal-delay', `${(index % 4) * 85}ms`);
      if (observer) observer.observe(element); else reveal(element);
    });
  };

  prepare();
  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
})();
